import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Flex } from 'foundations-components/components/Flex';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import { getButtonVariantProps } from 'pricing-pages-ui-components/utils/buttonVariantUtils';
import { useSelector } from 'react-redux';
import { useHighlightedProductsHeaderConfig } from '../../../constants/merchCards/HighlightedProductsHeaderConfig';
import HubProductCTA from '../../../features/purchaseMotions/components/HubProductCTA';
import MerchCardCTAOverridesWrapper from '../../../features/purchaseMotions/components/MerchCardCTAOverridesWrapper';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useHubPurchaseMotions } from '../../../features/purchaseMotions/hooks/useHubPurchaseMotions';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { useShouldShowTermToggleForMerchCard } from '../../../hooks/useShouldShowTermSelectorForMerchCard';
import { useAppSelector } from '../../../reducers';
import { isMerchCardCTAsDataLoadingSelector } from '../../../selectors/LoadingStateSelectors';
import { packagedQuantityBySkuIdSelector, productSelector } from '../../../selectors/ProductsSelectors';
import { getNavItemForProductLine } from '../../../utils/routingUtils';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtilsLegacy';
import { MerchCardLayoutUI } from '../../../v2/components/MerchCardLayoutUI';
import { ErrorBoundary } from '../../../v2/components/error-boundary/ErrorBoundary';
import { IncludedHubSpotCredits } from '../../../v2/components/merch-card/credits-display/IncludedHubSpotCredits';
import { MERCH_CARD } from '../../../v2/constants/tracking/TrackingSources';
import { MerchCardSubheadingContainer } from '../../../v2/containers/merch-card/headings-display/MerchCardSubheadingContainer';
import { MerchCardTitleContainer } from '../../../v2/containers/merch-card/headings-display/MerchCardTitleContainer';
import { isValidApiName } from '../../../v2/utils/typeguards';
import DetailedTermSelector from '../../common/TermSelectors/DetailedTermSelector';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import RequiredOnboardingSubtextContainer from '../../plan-builder/RequiredOnboardingSubtextContainer';
import { StandardFeaturesContainer } from '../features-display/features-display-containers/StandardFeaturesContainer';
import { PriceDisplayContainer } from '../price-display/PriceDisplayContainer';
import { IncludedSeatsDisplayContainerV1 } from '../seats-display/IncludedSeatsDisplayContainerV1';
import { SubtextDisplayContainerV1 } from '../subtext-display/SubtextDisplayContainerV1';
import { useSkuAdditionalUnits } from '../useSkuAdditionalUnits';
import { jsx as _jsx } from "react/jsx-runtime";
export const HubCard = ({
  productLevel,
  productLine,
  selectedTab,
  columnStart,
  Badge
}) => {
  const screen = getNavItemForProductLine(productLine);
  const isAppReady = useGlobalLoading();
  const isMobile = useIsMobile();
  const isMerchCardCTAsDataLoading = useSelector(isMerchCardCTAsDataLoadingSelector);
  const product = useSelector(state => productSelector(state, {
    productLine,
    productLevel
  }));
  const apiName = product === null || product === void 0 ? void 0 : product.apiName;
  const seleniumId = apiName || 'loading';
  const {
    additionalUnitSkuId,
    skuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const includedProductLevelHubspotCredits = useAppSelector(state => {
    if (!skuId) return null;
    return packagedQuantityBySkuIdSelector(state, {
      skuId,
      unitName: 'HUBSPOT_CREDITS'
    });
  });
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const configKeys = useHighlightedProductsHeaderConfig(productLine, productLevel);
  const {
    title = '',
    subtitle = ''
  } = configKeys || {};
  const shouldShowTermSelector = useShouldShowTermToggleForMerchCard(product);
  const purchaseMotions = useHubPurchaseMotions({
    skuId
  });
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  const Title = /*#__PURE__*/_jsx(MerchCardTitleContainer, {
    Title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: title
    }),
    Badge: Badge
  });
  const Subheading = /*#__PURE__*/_jsx(MerchCardSubheadingContainer, {
    Subheading: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: subtitle
    }),
    apiName: apiName
  });
  const Pricing = (() => {
    if (!skuId) return null;
    return /*#__PURE__*/_jsx(PriceDisplayContainer, {
      skuId: skuId,
      queryParamTerm: queryParamTerm,
      apiName: apiName
    });
  })();
  const Seats = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [20],
    widths: [140],
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "400",
      children: /*#__PURE__*/_jsx(IncludedSeatsDisplayContainerV1, {
        skuId: skuId
      })
    })
  });
  const Term = shouldShowTermSelector && isValidApiName(apiName) && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [41],
    widths: ['100%'],
    children: /*#__PURE__*/_jsx(DetailedTermSelector, {
      apiName: apiName,
      onChange: setQueryParamTerm,
      activeTerm: queryParamTerm,
      trackingSource: MERCH_CARD
    })
  });
  const Credits = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [17],
    widths: ['75%'],
    children: !!includedProductLevelHubspotCredits && /*#__PURE__*/_jsx(IncludedHubSpotCredits, {
      includedProductLevelHubspotCredits: includedProductLevelHubspotCredits,
      trackingSource: MERCH_CARD
    })
  });
  const Subtext = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [17],
    widths: ['75%'],
    children: /*#__PURE__*/_jsx(SubtextDisplayContainerV1, {
      additionalUnitSkuId: additionalUnitSkuId,
      queryParamTerm: queryParamTerm,
      productLine: productLine,
      productLevel: productLevel
    })
  });
  const CTAs = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: isMerchCardCTAsDataLoading,
    heights: [47],
    widths: ['100%'],
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      fallback: apiName ? /*#__PURE__*/_jsx(TalkToSalesFallback, {
        apiName: apiName,
        trackingSource: MERCH_CARD,
        screen: screen
      }) : null,
      children: skuId ? /*#__PURE__*/_jsx(MerchCardCTAOverridesWrapper, Object.assign({
        skuId: skuId,
        "data-selenium-id": `${MERCH_CARD}-${apiName}`,
        trackingSource: MERCH_CARD
      }, ctaVariantProps, {
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          justifyContent: "center",
          gap: "200",
          inlineSize: "100%",
          children: purchaseMotions.map((purchaseMotion, index) => {
            return /*#__PURE__*/_jsx("div", {
              children: /*#__PURE__*/_jsx(HubProductCTA, Object.assign({
                purchaseMotion: purchaseMotion,
                skuId: skuId,
                productLine: productLine,
                additionalUnitSkuId: additionalUnitSkuId,
                trackingSource: MERCH_CARD,
                use: index === 0 ? 'primary' : 'secondary',
                purchaseMotionLocation: "MERCH_CARD"
              }, ctaVariantProps))
            }, String(purchaseMotion));
          })
        })
      })) : null
    })
  });
  const Footer = skuId && /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    alignItems: "center",
    children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
      queryParamTerm: queryParamTerm,
      skuIds: [skuId],
      tab: selectedTab,
      trackingSource: MERCH_CARD,
      includesOnboardingCost: false
    })
  });
  return /*#__PURE__*/_jsx(MerchCardLayoutUI, {
    "data-selenium-id": `${seleniumId}-merch-card`,
    "data-test-id": `${seleniumId}-merch-card`,
    columnStart: columnStart,
    Title: Title,
    Subheading: Subheading,
    Pricing: Pricing,
    Seats: Seats,
    Term: Term,
    Credits: Credits,
    Subtext: Subtext,
    CTAs: CTAs,
    Features: /*#__PURE__*/_jsx(StandardFeaturesContainer, {
      productLine: productLine,
      productLevel: productLevel,
      productApiName: apiName,
      isCollapsible: isMobile
    }),
    Footer: Footer
  });
};